import { useState } from 'react'
import { ChevronDown, ExternalLink, Play, Globe } from 'lucide-react'
import { cn } from '@/utils'
import { getMediaTypeStyle } from './mediaTypeStyles'
import { cleanPassageText } from './cleanPassageText'
import { getExternalUrl } from './externalUrl'
import type { DisplaySource } from '@/types/source'

interface ExperienceSourceCardProps {
  source: DisplaySource
  onOpenSource: (source: DisplaySource) => void
  isFirst?: boolean
  isLast?: boolean
}

/** Build a contextual metadata line based on media type */
function getMetadataLine(source: DisplaySource): string {
  const style = getMediaTypeStyle(source.mediaType)
  switch (source.mediaType) {
    case 'video':
      return source.formattedTimeRange
        ? `${source.formattedTimeRange.replace(' - ', ' \u2013 ')} \u00b7 ${style.label}`
        : style.label
    case 'audio':
      return [
        source.formattedTimeRange?.replace(' - ', ' \u2013 '),
        source.speaker,
        style.label,
      ]
        .filter(Boolean)
        .join(' \u00b7 ')
    case 'scorm':
      return source.page_number != null
        ? `Lesson ${source.page_number} \u00b7 ${style.label}`
        : style.label
    case 'web': {
      // Show the domain name from original_url
      try {
        const domain = source.original_url ? new URL(source.original_url).hostname.replace(/^www\./, '') : null
        return domain ? `${domain} \u00b7 ${style.label}` : style.label
      } catch {
        return style.label
      }
    }
    case 'document': {
      // Derive specific document type label from filename extension
      const filename = source.source_filename?.toLowerCase() ?? ''
      let docLabel = style.label
      if (filename.endsWith('.pdf')) docLabel = 'PDF Document'
      else if (filename.endsWith('.docx') || filename.endsWith('.doc')) docLabel = 'Word Document'
      else if (filename.endsWith('.pptx') || filename.endsWith('.ppt')) docLabel = 'Presentation'
      else if (filename.endsWith('.xlsx') || filename.endsWith('.xls')) docLabel = 'Spreadsheet'
      else if (filename.endsWith('.md')) docLabel = 'Article'
      else if (filename.endsWith('.txt')) docLabel = 'Text Document'
      return [source.formattedPageNumber, docLabel].filter(Boolean).join(' \u00b7 ')
    }
    case 'image':
      return style.label
    default:
      return style.label
  }
}

/** Get the action button label per media type */
function getActionLabel(source: DisplaySource): string {
  switch (source.mediaType) {
    case 'video':
      return source.formattedTimeRange
        ? `Watch from ${source.formattedTimeRange.split(' - ')[0]}`
        : 'Watch Video'
    case 'audio':
      return source.formattedTimeRange
        ? `Listen from ${source.formattedTimeRange.split(' - ')[0]}`
        : 'Listen'
    case 'scorm':
      return 'Open Course Module'
    case 'image':
      return 'View Full Image'
    case 'web':
      return 'Visit Web Page'
    default:
      return 'Open Document'
  }
}

function getActionIcon(mediaType: string) {
  if (mediaType === 'video' || mediaType === 'audio') return Play
  if (mediaType === 'web') return Globe
  return ExternalLink
}

export function ExperienceSourceCard({
  source,
  onOpenSource,
  isFirst = false,
  isLast = false,
}: ExperienceSourceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [imageFailed, setImageFailed] = useState(false)

  // The card can't authenticate an <img> against proxy-type URLs (that
  // needs the viewer's blob fetch), and 'reference' URLs are opaque
  // azure:// strings — show the text excerpt for those instead of a
  // broken-image glyph.
  const imageEmbeddable =
    !!source.storage_url &&
    source.storage_url_type !== 'proxy' &&
    source.storage_url_type !== 'reference' &&
    !imageFailed
  const style = getMediaTypeStyle(source.mediaType)
  const ActionIcon = getActionIcon(source.mediaType)
  // Web sources open original_url directly (SA-204) — URL resolution
  // stays in getExternalUrl so a future capability can intercept the
  // open action. Null (no resolvable URL) falls back to the viewer.
  const webUrl = source.mediaType === 'web' ? getExternalUrl(source) : null
  const actionClassName = cn(
    'w-full mt-3 py-2 px-3 rounded-lg border text-xs font-medium flex items-center justify-center gap-1.5 hover:brightness-95 dark:hover:brightness-110 transition-[filter]',
    style.btnBg,
    style.btnBorder,
    style.btnText
  )

  return (
    <div
      className={cn(
        'group border border-b-0 border-border border-l-[3px] bg-card transition-colors hover:bg-accent/50',
        style.border,
        isFirst && 'rounded-t-xl',
        isLast && 'border-b rounded-b-xl'
      )}
    >
      {/* Compact header — always visible, clickable */}
      <button
        type="button"
        className="w-full text-left px-4 py-3.5 flex items-start gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-[inherit]"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Media type icon */}
        <div
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5',
            style.iconBg
          )}
        >
          <style.Icon className={cn('w-[18px] h-[18px]', style.iconText)} />
        </div>

        {/* Text content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[13px] font-semibold text-foreground truncate">
              {source.title}
            </span>
            <ChevronDown
              className={cn(
                'w-3.5 h-3.5 text-muted-foreground shrink-0 transition-transform duration-200 group-hover:text-foreground',
                isExpanded && 'rotate-180'
              )}
            />
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {getMetadataLine(source)}
          </div>
          {!isExpanded && (
            <div className="text-[12.5px] text-muted-foreground mt-1.5 line-clamp-2 leading-relaxed">
              {cleanPassageText(source.document)}
            </div>
          )}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4">
          <div className="border-t border-border pt-3.5">
            {/* Video preview */}
            {source.mediaType === 'video' && (
              <>
                <div className="bg-gray-900 rounded-lg aspect-video flex items-center justify-center relative overflow-hidden mb-3">
                  <div className="absolute inset-0 bg-linear-to-br from-violet-500/10 to-indigo-500/5" />
                  <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-xs flex items-center justify-center">
                    <Play className="w-5 h-5 text-white fill-white" />
                  </div>
                  {source.formattedTimeRange && (
                    <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-0.5 rounded-sm text-[11px] text-gray-300">
                      {source.formattedTimeRange.replace(' - ', ' \u2013 ')}
                    </div>
                  )}
                </div>
                <div className="bg-muted rounded-lg p-3 text-[12.5px] text-foreground leading-relaxed italic">
                  {source.speaker && (
                    <span className="text-violet-600 dark:text-violet-400 not-italic font-medium">
                      {source.speaker}:{' '}
                    </span>
                  )}
                  &ldquo;{cleanPassageText(source.document)}&rdquo;
                </div>
              </>
            )}

            {/* Audio preview */}
            {source.mediaType === 'audio' && (
              <>
                <div className={cn(style.iconBg, 'rounded-lg px-4 py-3 flex items-center gap-3 mb-3')}>
                  <div className={cn('w-8 h-8 rounded-full flex items-center justify-center shrink-0', style.btnBg)}>
                    <Play className={cn('w-3.5 h-3.5 fill-current', style.iconText)} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-[2px] h-6">
                      {Array.from({ length: 20 }, (_, i) => (
                        <div
                          key={i}
                          className={cn(
                            'w-[3px] rounded-xs',
                            i < 12 ? 'bg-orange-400' : 'bg-muted-foreground/40'
                          )}
                          style={{
                            height: `${8 + Math.sin(i * 0.8) * 10 + (i * 3) % 7}px`,
                            opacity: i < 12 ? 0.6 + (i % 3) * 0.15 : 0.4,
                          }}
                        />
                      ))}
                    </div>
                    {source.formattedTimeRange && (
                      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                        <span>{source.formattedTimeRange.split(' - ')[0]}</span>
                        <span>{source.formattedTimeRange.split(' - ')[1]}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-muted rounded-lg p-3 text-[12.5px] text-foreground leading-relaxed italic">
                  {source.speaker && (
                    <span className="text-orange-600 dark:text-orange-400 not-italic font-medium">
                      {source.speaker}:{' '}
                    </span>
                  )}
                  &ldquo;{cleanPassageText(source.document)}&rdquo;
                </div>
              </>
            )}

            {/* Image preview */}
            {source.mediaType === 'image' && !source.isExpired && imageEmbeddable && (
              <div className="rounded-lg overflow-hidden mb-3 bg-muted">
                <img
                  src={source.storage_url ?? undefined}
                  alt={source.title}
                  className="max-h-[200px] w-auto mx-auto object-contain"
                  loading="lazy"
                  onError={() => setImageFailed(true)}
                />
              </div>
            )}

            {/* Text content (document, web, scorm, non-embeddable image) */}
            {(source.mediaType === 'document' ||
              source.mediaType === 'web' ||
              source.mediaType === 'scorm' ||
              (source.mediaType === 'image' &&
                (source.isExpired || !imageEmbeddable))) && (
              <div className="bg-muted rounded-lg p-3.5 text-[13px] text-foreground leading-[1.65] whitespace-pre-wrap">
                {cleanPassageText(source.document)}
              </div>
            )}

            {/* Action: direct link for web sources, viewer button otherwise */}
            {webUrl ? (
              <a
                href={webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={actionClassName}
                onClick={(e) => e.stopPropagation()}
              >
                <ActionIcon className="w-3.5 h-3.5" />
                {getActionLabel(source)}
              </a>
            ) : (
              <button
                type="button"
                className={actionClassName}
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenSource(source)
                }}
              >
                <ActionIcon className="w-3.5 h-3.5" />
                {getActionLabel(source)}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
